/* prettier-ignore-file */
import React, { useState, useEffect, useRef } from 'react';
import { View, TextInput, StyleSheet, Text, TouchableOpacity, ScrollView } from 'react-native';

const GOOGLE_MAPS_API_KEY = 'AIzaSyDnMrIVkx-6V0Brm4pxezeOVcTOELsTmdQ';

const GoogleAddressInput = ({ value, onChangeText, placeholder, style, multiline, onAddressSelected, editable = true, onFocus, onBlur, isValidated, disableDropdown = false }) => {
  const [inputValue, setInputValue] = useState(value || '');
  const [predictions, setPredictions] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const autocompleteService = useRef(null);
  const debounceTimer = useRef(null);

  useEffect(() => {
    // Load Google Maps API script if not already loaded
    if (!window.google || !window.google.maps || !window.google.maps.places) {
      const script = document.createElement('script');
      script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_API_KEY}&libraries=places`;
      script.async = true;
      script.defer = true;
      script.onload = () => {
        initAutocomplete();
      };
      document.head.appendChild(script);
    } else {
      initAutocomplete();
    }
  }, []);

  useEffect(() => {
    // Sync external value changes with internal state
    if (value !== inputValue) {
      setInputValue(value || '');
    }
  }, [value]);

  const initAutocomplete = () => {
    if (window.google && window.google.maps && window.google.maps.places) {
      autocompleteService.current = new window.google.maps.places.AutocompleteService();
    }
  };

  const handleInputChange = (text) => {
    setInputValue(text);
    onChangeText(text);

    // Clear existing timer
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }

    // Hide dropdown if text is empty
    if (!text.trim()) {
      setPredictions([]);
      setShowDropdown(false);
      return;
    }

    // Debounce the API call (skip if dropdown is disabled)
    if (!disableDropdown) {
      debounceTimer.current = setTimeout(() => {
        if (autocompleteService.current && text.trim().length > 2) {
          autocompleteService.current.getPlacePredictions(
            {
              input: text,
              componentRestrictions: { country: 'us' }, // Restrict to US addresses
            },
            (predictions, status) => {
              if (status === window.google.maps.places.PlacesServiceStatus.OK && predictions) {
                setPredictions(predictions);
                setShowDropdown(true);
              } else {
                setPredictions([]);
                setShowDropdown(false);
              }
            }
          );
        }
      }, 300); // Wait 300ms after user stops typing
    }
  };

  const handleSelectPrediction = (prediction) => {
    const selectedAddress = prediction.description;
    setShowDropdown(false);
    setPredictions([]);
    setInputValue(selectedAddress);
    if (onAddressSelected) {
      onAddressSelected(selectedAddress);
    } else {
      onChangeText(selectedAddress);
    }
  };

  const isDropdownOpen = !disableDropdown && showDropdown && predictions.length > 0;

  return (
    // Each instance's container is a stacking context carrying the SAME zIndex, so with
    // two address inputs stacked the later sibling paints over the earlier one and
    // swallows its dropdown — the dropdown's own zIndex only orders things *inside* its
    // own container, so raising that further can never help. Lifting the OPEN instance
    // above its siblings is what actually fixes it; only one can be open at a time.
    <View style={[styles.container, isDropdownOpen && styles.containerOpen, style]}>
      <TextInput
        style={[
          styles.input,
          multiline && styles.inputMultiline,
          !editable && styles.inputDisabled,
          isValidated === true && styles.inputValidated,
          isValidated === false && styles.inputNotValidated,
        ]}
        placeholder={placeholder || 'Start typing an address...'}
        placeholderTextColor="#5C6B68"
        value={inputValue}
        onChangeText={editable ? handleInputChange : undefined}
        autoCapitalize="words"
        multiline={multiline}
        editable={editable}
        onFocus={onFocus}
        onBlur={() => {
          setShowDropdown(false);
          setPredictions([]);
          onBlur?.();
        }}
      />
      
      {isDropdownOpen && (
        <View style={styles.dropdownContainer}>
          <ScrollView 
            style={styles.dropdown}
            keyboardShouldPersistTaps="handled"
            nestedScrollEnabled={true}
          >
            {predictions.map((prediction) => (
              <TouchableOpacity
                key={prediction.place_id}
                style={styles.predictionItem}
                onPress={() => handleSelectPrediction(prediction)}
                // Clicking a prediction moves focus off the TextInput, which fires
                // onBlur (on mousedown, before onPress's click) and hides this dropdown
                // out from under the click — so the selection never registers. Blocking
                // the default here keeps focus on the input so onPress still fires.
                onMouseDown={(e) => e.preventDefault()}
              >
                <Text style={styles.predictionText} numberOfLines={2}>
                  {prediction.description}
                </Text>
              </TouchableOpacity>
            ))}
          </ScrollView>
        </View>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    position: 'relative',
    zIndex: 99999,
    overflow: 'visible',
  },
  // Applied only while this instance's dropdown is showing, so it outranks any
  // address input rendered after it.
  containerOpen: {
    zIndex: 999999,
  },
  input: {
    height: 44,
    borderWidth: 1,
    borderColor: '#DDE3E2',
    borderRadius: 8,
    paddingHorizontal: 12,
    fontSize: 16,
    backgroundColor: '#FFFFFF',
    width: '100%',
  },
  inputMultiline: {
    minHeight: 88,
    paddingVertical: 12,
    textAlignVertical: 'top',
  },
  inputDisabled: {
    backgroundColor: '#F6F8F7',
    color: '#2E3A38',
  },
  inputValidated: {
    backgroundColor: '#E4F4EC', // Light green
  },
  inputNotValidated: {
    backgroundColor: '#FBEAE9', // Light red
  },
  dropdownContainer: {
    position: 'absolute',
    top: 48,
    left: 0,
    right: 0,
    zIndex: 100000,
    marginTop: 4,
    overflow: 'visible',
  },
  dropdown: {
    maxHeight: 200,
    backgroundColor: '#FDF3E3',
    borderWidth: 2,
    borderColor: '#B4761B',
    borderRadius: 6,
    boxShadow: "0 4px 12px rgba(15, 26, 25, 0.1), 0 28px 64px rgba(15, 26, 25, 0.18)",
    elevation: 30,
    overflow: 'hidden',
  },
  predictionItem: {
    padding: 12,
    borderBottomWidth: 1,
    borderBottomColor: '#EDF1F0',
  },
  predictionText: {
    fontSize: 14,
    color: '#2E3A38',
  },
});

export default GoogleAddressInput;


